import React, { useEffect, useRef, useState } from 'react';
import resume from '../../data/resume';
import ScrollIntro from './ScrollIntro';
import ProjectGallery from '../ProjectGallery';
import Lightbox from '../Lightbox';
import '../../styles/professional.css';

const MailIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" />
  </svg>
);
const LinkedinIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" />
  </svg>
);
const ExternalIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3h7v7" /><path d="M10 14 21 3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
  </svg>
);

const Reveal = ({ children, className = '', as: Tag = 'div', ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('in-view');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in-view');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`pro-reveal ${className}`} {...rest}>
      {children}
    </Tag>
  );
};

const ProfessionalView = () => {
  const { name, tagline, title, about, location, email, phone, linkedin, linkedinLabel,
    resumeUrl, photo, available, skills, experience, projects, education, certificates } = resume;

  // { images, index } while the screenshot viewer is open, null otherwise.
  const [lightbox, setLightbox] = useState(null);

  const nameParts = name.split(' ');

  return (
    <div className="pro">
      <ScrollIntro name={name} phrase={tagline} />
      {/* Top navigation */}
      <nav className="pro-nav">
        <div className="pro-wrap pro-nav-inner">
          <span className="pro-mark">FS<span className="dot">.</span></span>
          <div className="pro-nav-links">
            <a href="#work">Work</a>
            <a href="#projects">Projects</a>
            <a href="#skills">Skills</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="pro-wrap">
        <div className="pro-hero">
          <div>
            <Reveal as="span" className="pro-eyebrow">Available for opportunities</Reveal>
            <Reveal as="h1" className="pro-hero-name" style={{ transitionDelay: '60ms' }}>
              <span className="ln">{nameParts[0]}</span>
              <span className="ln accent">{nameParts.slice(1).join(' ')}</span>
            </Reveal>
            <Reveal className="pro-hero-title" style={{ transitionDelay: '120ms' }}>{title}</Reveal>
            <Reveal className="pro-hero-about" style={{ transitionDelay: '180ms' }}>{about}</Reveal>
            <Reveal className="pro-cta" style={{ transitionDelay: '240ms' }}>
              <a className="pro-btn pro-btn-primary" href={`mailto:${email}`}><MailIcon /> Get in touch</a>
              <a className="pro-btn pro-btn-ghost" href={resumeUrl} target="_blank" rel="noopener noreferrer"><DownloadIcon /> Résumé</a>
              <a className="pro-btn pro-btn-ghost" href={linkedin} target="_blank" rel="noopener noreferrer"><LinkedinIcon /> LinkedIn</a>
            </Reveal>
            <Reveal className="pro-meta" style={{ transitionDelay: '300ms' }}>
              <span className="avail"><span className="pro-dot" />{available ? 'Open to work' : 'Currently engaged'}</span>
              <span>◦ {location}</span>
              <span>◦ {phone}</span>
            </Reveal>
          </div>
          <Reveal className="pro-photo-wrap" style={{ transitionDelay: '160ms' }}>
            <div className="pro-photo-frame">
              <img src={photo} alt={name} loading="eager" />
            </div>
            <span className="pro-photo-tag">{location}</span>
          </Reveal>
        </div>
      </header>

      {/* Experience */}
      <section className="pro-section" id="work">
        <div className="pro-wrap">
          <Reveal className="pro-section-head">
            <div>
              <span className="pro-eyebrow">Experience</span>
              <h2 className="pro-h2">Where I've built</h2>
            </div>
            <span className="pro-section-num">01 / 04</span>
          </Reveal>
          <div>
            {experience.map((job, i) => (
              <Reveal className="pro-exp-item" key={i} style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="pro-exp-when">
                  {job.dates}
                  <span className="pro-exp-company">{job.company}</span>
                </div>
                <div>
                  <h3 className="pro-exp-role">{job.role}</h3>
                  <ul className="pro-exp-points">
                    {job.points.map((p, j) => <li key={j}>{p}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="pro-section" id="projects">
        <div className="pro-wrap">
          <Reveal className="pro-section-head">
            <div>
              <span className="pro-eyebrow">Selected work</span>
              <h2 className="pro-h2">Projects</h2>
            </div>
            <span className="pro-section-num">02 / 04</span>
          </Reveal>
          <div className="pro-projects-grid">
            {projects.map((p, i) => (
              <Reveal className="pro-card" key={i} style={{ transitionDelay: `${(i % 2) * 80}ms` }}>
                <ProjectGallery
                  images={p.images}
                  onOpen={(idx) => setLightbox({ images: p.images, index: idx })}
                />
                <span className="pro-card-index">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="pro-card-title">{p.title}</h3>
                {p.subtitle && <p className="pro-card-sub">{p.subtitle}</p>}
                <p className="pro-card-desc">{p.description}</p>
                <div className="pro-tags">
                  {p.tech.map((t, j) => <span className="pro-tag" key={j}>{t}</span>)}
                </div>
                {p.links && p.links.length > 0 && (
                  <div className="pro-card-links">
                    {p.links.map((l) => (
                      <a className="pro-card-link" key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">
                        <ExternalIcon /> {l.label}
                      </a>
                    ))}
                  </div>
                )}
              </Reveal>
            ))}
          </div>
          <Lightbox
            images={lightbox ? lightbox.images : null}
            index={lightbox ? lightbox.index : null}
            onClose={() => setLightbox(null)}
            onNavigate={(idx) => setLightbox((s) => (s ? { ...s, index: idx } : s))}
          />
        </div>
      </section>

      {/* Skills */}
      <section className="pro-section" id="skills">
        <div className="pro-wrap">
          <Reveal className="pro-section-head">
            <div>
              <span className="pro-eyebrow">Toolkit</span>
              <h2 className="pro-h2">Skills &amp; stack</h2>
            </div>
            <span className="pro-section-num">03 / 04</span>
          </Reveal>
          <div className="pro-skills-grid">
            {skills.map((s, i) => (
              <Reveal className="pro-skill-card" key={i} style={{ transitionDelay: `${(i % 3) * 70}ms` }}>
                <div className="pro-skill-cat">{s.category}</div>
                <div className="pro-skill-items">
                  {s.items.map((it, j) => <span className="pro-chip" key={j}>{it}</span>)}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Education + Certificates */}
      <section className="pro-section">
        <div className="pro-wrap">
          <Reveal className="pro-section-head">
            <div>
              <span className="pro-eyebrow">Background</span>
              <h2 className="pro-h2">Education &amp; credentials</h2>
            </div>
            <span className="pro-section-num">04 / 04</span>
          </Reveal>
          <div className="pro-two-col">
            <Reveal>
              <div className="pro-block-label">Education</div>
              {education.map((e, i) => (
                <div className="pro-edu" key={i}>
                  <h3 className="pro-edu-degree">{e.degree}</h3>
                  <p className="pro-edu-inst">{e.institution}</p>
                  <div className="pro-edu-meta">
                    <span>{e.dates}</span>
                    <span>CGPA {e.cgpa}</span>
                  </div>
                  <p className="pro-edu-desc">{e.description}</p>
                </div>
              ))}
            </Reveal>
            <Reveal style={{ transitionDelay: '80ms' }}>
              <div className="pro-block-label">Certifications</div>
              {certificates.map((c, i) => (
                <div className="pro-cert" key={i}>
                  <span className="pro-cert-mark">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <p className="pro-cert-title">{c.title} <span className="pro-cert-issuer">— {c.issuer}</span></p>
                    <p className="pro-cert-desc">{c.description}</p>
                  </div>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="pro-contact" id="contact">
        <div className="pro-wrap">
          <Reveal>
            <span className="pro-eyebrow" style={{ justifyContent: 'center' }}>Get in touch</span>
            <h2 className="pro-contact-h">Let's build something reliable.</h2>
            <a className="pro-contact-mail" href={`mailto:${email}`}>{email}</a>
            <div className="pro-contact-links">
              <a className="pro-btn pro-btn-ghost" href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
              <a className="pro-btn pro-btn-ghost" href={linkedin} target="_blank" rel="noopener noreferrer"><LinkedinIcon /> {linkedinLabel}</a>
              <a className="pro-btn pro-btn-ghost" href={resumeUrl} target="_blank" rel="noopener noreferrer"><DownloadIcon /> Download résumé</a>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="pro-footer">
        <div className="pro-wrap pro-footer-inner">
          <span>© 2026 {name}</span>
          <span>Designed &amp; built end to end · React</span>
        </div>
      </footer>
    </div>
  );
};

export default ProfessionalView;
